import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { deliveryZoneCodes } from '@noths/polaris-client-localisation';

import type { Facet, FacetOption } from 'src/redux/productsQuery/types/Facet';
import { BrowseDataAPIFilterUID } from 'src/services/browse-data-api/constants/BrowseDataAPIFilterUID';
import type { BrowseDataAPIFilter } from 'src/services/browse-data-api/types/BrowseDataAPIFilter';
import { parseToNumberOrNull } from 'src/utils/parseToNumberOrNull';

export const PRESET_PRICE_RANGES = [
  { min: 0, max: 10 },
  { min: 10, max: 20 },
  { min: 20, max: 50 },
  { min: 50, max: 100 },
  { min: 100, max: null },
];

const getFacetType = (facetUID: BrowseDataAPIFilterUID) => {
  switch (facetUID) {
    case BrowseDataAPIFilterUID.Age:
    case BrowseDataAPIFilterUID.BeautyIngredients:
    case BrowseDataAPIFilterUID.BodyArea:
    case BrowseDataAPIFilterUID.BraceletStyle:
    case BrowseDataAPIFilterUID.Color:
    case BrowseDataAPIFilterUID.CufflinkStyle:
    case BrowseDataAPIFilterUID.Delivery:
    case BrowseDataAPIFilterUID.Discount:
    case BrowseDataAPIFilterUID.EarringStyle:
    case BrowseDataAPIFilterUID.Fairtrade:
    case BrowseDataAPIFilterUID.Finish:
    case BrowseDataAPIFilterUID.FrameColor:
    case BrowseDataAPIFilterUID.Framing:
    case BrowseDataAPIFilterUID.Gender:
    case BrowseDataAPIFilterUID.Material:
    case BrowseDataAPIFilterUID.NecklaceStyle:
    case BrowseDataAPIFilterUID.Organic:
    case BrowseDataAPIFilterUID.Orientation:
    case BrowseDataAPIFilterUID.PreciousStone:
    case BrowseDataAPIFilterUID.RingStyle:
    case BrowseDataAPIFilterUID.SecondaryColor:
    case BrowseDataAPIFilterUID.SecondaryMaterial:
    case BrowseDataAPIFilterUID.Size:
    case BrowseDataAPIFilterUID.SpecialFeatures:
    case BrowseDataAPIFilterUID.WineRegion:
    case BrowseDataAPIFilterUID.WorldCuisine:
      return 'multi';
    case BrowseDataAPIFilterUID.Price:
      return 'price';
    case BrowseDataAPIFilterUID.DeliveryZones:
    case BrowseDataAPIFilterUID.Personalisation:
      return 'single';
    case BrowseDataAPIFilterUID.Categories:
    case BrowseDataAPIFilterUID.Subcategories:
      return 'tree';
    default:
      return null;
  }
};

export const adaptPriceFacet = (facet: BrowseDataAPIFilter): Facet => {
  const manualPriceRange = {
    max: parseToNumberOrNull(facet.options[1].value as string),
    min: parseToNumberOrNull(facet.options[0].value as string),
  };

  const priceRangesWithManualRange = [...PRESET_PRICE_RANGES, manualPriceRange];

  const priceFacet: Facet = {
    active: true,
    valueType: 'numeric',
    options: priceRangesWithManualRange.map((range) => {
      const hasAnyValue = range.min !== null || range.max !== null;
      const sameAsManual = range.min === manualPriceRange.min && range.max === manualPriceRange.max;
      const bothValuesZero = range.min === 0 && range.max === 0;
      const active = hasAnyValue && sameAsManual && !bothValuesZero;

      return {
        value: null,
        title: `${range.min}-${range.max}`,
        active: active,
        count: null,
        minValue: range.min,
        maxValue: range.max,
      } as FacetOption;
    }),
    title: facet.title,
    type: 'price',
    key: facet.uid,
  };

  return priceFacet;
};

export const adaptDeliveryZoneFacet = (
  filter: BrowseDataAPIFilter,
  userDeliveryZone: DeliveryZoneCode,
) => {
  const userDeliveryZoneCode =
    Object.keys(deliveryZoneCodes).find((key) => deliveryZoneCodes[key] === userDeliveryZone) || -1;
  const { defaultOption, expandedByDefault, uid, ...restOfFilter } = filter;
  const hasActiveOption = restOfFilter.options.some((option) => option.active);

  return {
    ...restOfFilter,
    key: uid,
    options: restOfFilter.options.map((option) => {
      const isUserDeliveryZone = option.value === userDeliveryZoneCode;
      const { activeCount, tooltip, uid, ...restOfOption } = option;

      return {
        ...restOfOption,
        pseudoActive: !hasActiveOption && isUserDeliveryZone,
        count: null,
      };
    }),
    type: 'single',
  };
};

export const adaptBrowseFilters = (
  browseFilters: BrowseDataAPIFilter[],
  userDeliveryZone: DeliveryZoneCode,
): Facet[] => {
  return browseFilters
    .map((filter) => {
      const { defaultOption, expandedByDefault, uid, ...restOfFilter } = filter;

      if (filter.uid === BrowseDataAPIFilterUID.Price) {
        return adaptPriceFacet(filter);
      }
      if (filter.uid === BrowseDataAPIFilterUID.DeliveryZones) {
        return adaptDeliveryZoneFacet(filter, userDeliveryZone);
      }

      const type = getFacetType(filter.uid as BrowseDataAPIFilterUID);

      if (type === null) {
        return null;
      }

      return {
        ...restOfFilter,
        type,
        key: uid,
        options: filter.options.map((option) => {
          const { activeCount, tooltip, uid, ...restOfOption } = option;

          return {
            ...restOfOption,
            count: activeCount ?? null,
            minValue: option.minValue ?? null,
            maxValue: option.maxValue ?? null,
          };
        }),
      };
    })
    .filter((facet): facet is Facet => facet !== null);
};
